import { useEffect } from 'react';

import { tauriWindowController } from '@/tauri/controller/window';
import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';

export function useDesktopZoomShortcuts(): void {
  useEffect(() => {
    if (!isTauri()) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      // AltGr is reported as ctrlKey + altKey together (Windows), which would
      // otherwise be indistinguishable from a real Ctrl+Alt combo and wrongly
      // disqualify layouts where AltGr produces '=' / '-'.
      const isAltGraph =
        typeof event.getModifierState === 'function' &&
        event.getModifierState('AltGraph');
      if ((!event.metaKey && !event.ctrlKey) || (event.altKey && !isAltGraph)) return;
      if (event.repeat) return;

      // Match both the produced character (event.key) and the physical key
      // (event.code). The native macOS menu accelerators bind to the physical
      // key, so on non-US/Dvorak/Colemak layouts where a symbol's key differs,
      // event.key alone would silently desync from the menu.
      let command: (() => Promise<number>) | undefined;
      if (event.key === '=' || event.key === '+' || event.code === 'Equal') {
        command = () => tauriWindowController.zoom_in();
      } else if (event.key === '-' || event.key === '_' || event.code === 'Minus') {
        command = () => tauriWindowController.zoom_out();
      } else if (event.key === '0' || event.code === 'Digit0') {
        command = () => tauriWindowController.zoom_reset();
      } else {
        return;
      }

      // The webview sees macOS key equivalents before the native menu, so this
      // also prevents the matching menu accelerator from applying zoom twice.
      event.preventDefault();
      void command().catch((error) => {
        logger.error('[desktop-zoom] Failed to update zoom', error);
      });
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, []);
}
